import React from 'react';

import { Box, Chip, Tooltip } from '@mui/material';

import { CommissionsConditionTypes, labelForUserTagValue, ruleConditionsToTextMap } from '~/consts/agentHub';

import type { CommissionFlightCarrierRule } from '~/types/services/agentHub';

import type { FlightLocationAdditionalInfo } from '~/utils/commissionFlightLocations';

type CommissionRuleValue = number | CommissionFlightCarrierRule | { airlineCode?: string; airlineName?: string };

interface Props {
  rules: Record<string, Array<CommissionRuleValue>>;
  /** Flight / bundle extras from API (departure & arrival locations live here, not in `rules`). */
  additionalInfo?: FlightLocationAdditionalInfo;
  agents?: Record<string, string>;
  agencies?: Record<string, string>;
}

interface MappedRules {
  agents?: Record<string, string>;
  agencies?: Record<string, string>;
}

function commissionRuleTypeLabel(key: string): string {
  if (key === 'carrier') {
    return ruleConditionsToTextMap.get('flightCarrier') ?? 'Flight carriers';
  }
  if (key === 'flightDeparture') {
    return ruleConditionsToTextMap.get('departure') ?? 'Departure location';
  }
  if (key === 'flightArrival') {
    return ruleConditionsToTextMap.get('arrival') ?? 'Arrival location';
  }
  if (key === 'flightDepartureDate') {
    return ruleConditionsToTextMap.get('flightDepartureDate') ?? 'Flight departure date';
  }
  if (key === 'flightReturnDate') {
    return ruleConditionsToTextMap.get('flightReturnDate') ?? 'Flight return date';
  }
  return ruleConditionsToTextMap.get(key as CommissionsConditionTypes) ?? key;
}

function formatCommissionRuleValue(rule: CommissionRuleValue): string {
  if (typeof rule !== 'object' || rule == null) {
    return String(rule);
  }
  const code = 'code' in rule ? rule.code : rule.airlineCode ?? '';
  const name = 'name' in rule ? rule.name : rule.airlineName ?? code;
  if (name && code && name !== code) {
    return `${name} (${code})`;
  }
  return code || name || '';
}

function getRuleInfo(key: string, rules: Array<CommissionRuleValue>) {
  switch (key) {
    case 'margin': {
      const [low, high] = rules;
      if (low === undefined || high === undefined) {
        return `${rules.length}`;
      }
      return `${formatCommissionRuleValue(low)}% - ${formatCommissionRuleValue(high)}%`;
    }
    case 'minTotalSpend':
      return rules[0] != null ? String(rules[0]) : '';
    default:
      return `${rules.length}`;
  }
}

function formatAdditionalLocation(loc: FlightLocationAdditionalInfo['departure'] | undefined): string {
  if (!loc) {
    return '';
  }
  return [loc.city, loc.country].filter(Boolean).join(', ');
}

function getRuleText(key: string, rules: Array<CommissionRuleValue>, mappedRules: MappedRules) {
  switch (key) {
    case 'agent': {
      const agentsMap = mappedRules.agents;
      if (!agentsMap) {
        return rules.map(formatCommissionRuleValue).join(' • ');
      }
      return rules
        .map((rule) => (typeof rule === 'string' ? agentsMap[rule] ?? rule : formatCommissionRuleValue(rule)))
        .join(' • ');
    }
    case 'tags': {
      return rules
        .map((rule) => (typeof rule === 'string' ? labelForUserTagValue(rule) : formatCommissionRuleValue(rule)))
        .join(' • ');
    }
    case 'agency': {
      const agenciesMap = mappedRules.agencies;
      if (!agenciesMap) {
        return rules.map(formatCommissionRuleValue).join(' • ');
      }
      return rules
        .map((rule) => (typeof rule === 'string' ? agenciesMap[rule] ?? rule : formatCommissionRuleValue(rule)))
        .join(' • ');
    }
    default:
      return rules.map(formatCommissionRuleValue).join(' • ');
  }
}

export function AgentHubCommissionRules({ rules, additionalInfo, agents, agencies }: Props) {
  const extraChips: Array<{ key: string; label: string; title: string }> = [];
  if (
    !rules.flightDeparture?.length &&
    additionalInfo?.departure &&
    formatAdditionalLocation(additionalInfo.departure)
  ) {
    const text = formatAdditionalLocation(additionalInfo.departure);
    extraChips.push({
      key: 'additionalInfo-departure',
      label: `Departure: ${text}`,
      title: text,
    });
  }
  if (!rules.flightArrival?.length && additionalInfo?.arrival && formatAdditionalLocation(additionalInfo.arrival)) {
    const text = formatAdditionalLocation(additionalInfo.arrival);
    extraChips.push({
      key: 'additionalInfo-arrival',
      label: `Arrival: ${text}`,
      title: text,
    });
  }

  return (
    <Box display="flex" flexWrap="wrap" gap={1}>
      {Object.entries(rules).map(([key, rules]: [string, Array<CommissionRuleValue>]) => (
        <Tooltip key={key} title={getRuleText(key, rules, { agents, agencies })}>
          <Chip
            sx={{ backgroundColor: '#00000014' }}
            label={`${commissionRuleTypeLabel(key)} (${getRuleInfo(key, rules)})`}
            variant="filled"
          />
        </Tooltip>
      ))}
      {extraChips.map((chip) => (
        <Tooltip key={chip.key} title={chip.title}>
          <Chip sx={{ backgroundColor: '#00000014' }} label={chip.label} variant="filled" />
        </Tooltip>
      ))}
    </Box>
  );
}
